import { TabBadgeUpdate, NavigationContextData } from '@/types/navigation';

/**
 * BadgeManager handles badge calculations and updates for navigation tabs
 */
export class BadgeManager {
  
  /**
   * Calculate badges based on context data
   */
  static calculateBadgesFromContext(
    contextData: NavigationContextData
  ): TabBadgeUpdate[] {
    const updates: TabBadgeUpdate[] = [];

    // Discover tab: Show news count
    if (contextData.newsCount > 0) {
      updates.push({
        tabId: 'discover',
        badge: contextData.newsCount > 99 ? '99+' : contextData.newsCount.toString()
      });
    } else {
      updates.push({ tabId: 'discover', badge: null });
    }

    // Conversations tab: Show conversation count
    if (contextData.conversationCount > 0) {
      updates.push({
        tabId: 'conversations',
        badge: contextData.conversationCount > 99 ? '99+' : contextData.conversationCount.toString()
      });
    } else {
      updates.push({ tabId: 'conversations', badge: null });
    }

    // Settings tab: Show issues or notifications
    let settingsBadge: string | number | null = null;
    
    // Priority 1: Location issues
    if (!contextData.location.latitude || !contextData.location.longitude) {
      settingsBadge = '!';
    }
    // Priority 2: Unread notifications
    else if (contextData.unreadNotifications > 0) {
      settingsBadge = contextData.unreadNotifications > 99 ? '99+' : contextData.unreadNotifications.toString();
    }
    
    updates.push({ tabId: 'settings', badge: settingsBadge });

    return updates;
  }

  /**
   * Format badge value for display
   */
  static formatBadgeForDisplay(badge: number | string | undefined): string {
    if (badge === undefined || badge === null || badge === '') {
      return '';
    }
    
    if (typeof badge === 'number') {
      return badge > 99 ? '99+' : badge.toString();
    }
    
    return badge.toString();
  }

  /**
   * Determine badge color/style based on type
   */
  static getBadgeStyle(badge: number | string | undefined): {
    variant: 'default' | 'destructive' | 'outline' | 'secondary';
    className?: string;
  } {
    if (!badge) {
      return { variant: 'default' };
    }

    // Error/warning badges
    if (badge === '!' || badge === 'error') {
      return { 
        variant: 'destructive',
        className: 'animate-pulse'
      };
    }

    // High numbers
    if (typeof badge === 'string' && badge.includes('99+')) {
      return { 
        variant: 'default',
        className: 'font-bold'
      };
    }

    // Normal count badges
    return { variant: 'default' };
  }

  /**
   * Check if badge should be shown
   */
  static shouldShowBadge(badge: number | string | undefined): boolean {
    return badge !== undefined && 
           badge !== null && 
           badge !== '' && 
           badge !== 0;
  }

  /**
   * Merge multiple badge updates, handling conflicts
   */
  static mergeBadgeUpdates(
    existingBadges: Record<string, number | string>,
    updates: TabBadgeUpdate[]
  ): Record<string, number | string> {
    const result = { ...existingBadges };

    updates.forEach(({ tabId, badge }) => {
      if (badge === null || badge === undefined) {
        delete result[tabId];
      } else {
        result[tabId] = badge;
      }
    });

    return result;
  }

  /**
   * Validate badge value
   */
  static validateBadge(badge: number | string | null | undefined): {
    isValid: boolean;
    sanitized?: number | string | null;
    error?: string;
  } {
    if (badge === null || badge === undefined || badge === '') {
      return { isValid: true, sanitized: null };
    }

    if (typeof badge === 'number') {
      if (badge < 0) {
        return { 
          isValid: false, 
          error: 'Badge number cannot be negative' 
        };
      }
      return { isValid: true, sanitized: badge };
    }

    if (typeof badge === 'string') {
      // Allow special indicators
      if (['!', 'error', 'warning'].includes(badge)) {
        return { isValid: true, sanitized: badge };
      }

      // Allow number strings and formatted counts
      if (/^\d+$/.test(badge) || /^\d+\+$/.test(badge)) {
        return { isValid: true, sanitized: badge };
      }

      return { 
        isValid: false, 
        error: 'Badge string must be a number, formatted count (e.g., "99+"), or special indicator' 
      };
    }

    return { 
      isValid: false, 
      error: 'Badge must be number, string, null, or undefined' 
    };
  }

  /**
   * Create context data from app state selectors
   */
  static createContextFromState(state: {
    location?: any;
    news?: any;
    conversations?: any;
    notifications?: any;
  }): NavigationContextData {
    const location = state.location?.currentLocation;
    const news = state.news?.newsContext;
    const conversations = state.conversations;
    
    return {
      location: {
        latitude: location?.coordinates?.lat || 0,
        longitude: location?.coordinates?.lng || 0,
        city: location?.locationInfo?.city,
        weather: location?.weather?.condition,
      },
      newsCount: news?.totalItems || 0,
      conversationCount: conversations?.totalCount || 0,
      unreadNotifications: 0, // Would come from notifications slice
    };
  }

  /**
   * Get badge accessibility label
   */
  static getBadgeAccessibilityLabel(
    tabLabel: string,
    badge: number | string | undefined
  ): string {
    if (!this.shouldShowBadge(badge)) {
      return `${tabLabel} tab`;
    }

    const badgeText = this.formatBadgeForDisplay(badge);
    
    if (badge === '!') {
      return `${tabLabel} tab, attention required`;
    }

    if (typeof badge === 'number' || /^\d+/.test(badgeText)) {
      const count = typeof badge === 'number' ? badge : parseInt(badgeText);
      return `${tabLabel} tab, ${count} ${count === 1 ? 'item' : 'items'}`;
    }

    return `${tabLabel} tab, ${badgeText}`;
  }

  /**
   * Cleanup old badges (remove stale entries)
   */
  static cleanupBadges(
    badges: Record<string, number | string>,
    validTabIds: string[]
  ): Record<string, number | string> {
    const cleaned: Record<string, number | string> = {};
    
    Object.entries(badges).forEach(([tabId, badge]) => {
      if (validTabIds.includes(tabId) && this.shouldShowBadge(badge)) {
        cleaned[tabId] = badge;
      }
    });

    return cleaned;
  }
}

export default BadgeManager;